import { useRef, useEffect, useState, useCallback } from 'react';

interface SeamlessVideoProps {
  src: string;
  className?: string;
  style?: React.CSSProperties;
  fadeDuration?: number;
  onReady?: () => void;
  onError?: () => void;
}

/**
 * SeamlessVideo — truly invisible loop using dual <video> z-index swap.
 *
 * How it works:
 * 1. Video A plays on top (z-index 2), Video B sits behind (z-index 1).
 * 2. Both videos are always at full opacity — no dissolve / crossfade.
 * 3. ~5 s before Video A ends, Video B gets its `src` assigned and buffers.
 * 4. ~150 ms before Video A ends, Video B starts playing from t = 0.
 * 5. At the exact end of Video A we instantly swap z-index:
 *    A drops to 1, B jumps to 2. Because B just started, the viewer
 *    sees frame ≈ 0 of B immediately after the last frame of A —
 *    identical to a native loop but without the browser's internal
 *    seek glitch.
 * 6. Video A is paused (saves resources) and the roles reverse for
 *    the next loop.
 *
 * The result is a perfectly invisible loop for any video.
 */
export function SeamlessVideo({
  src,
  className = '',
  style,
  fadeDuration = 1.5,
  onReady,
  onError,
}: SeamlessVideoProps) {
  const videoARef = useRef<HTMLVideoElement>(null);
  const videoBRef = useRef<HTMLVideoElement>(null);
  const [hasFirstFrame, setHasFirstFrame] = useState(false);
  const [showTapHint, setShowTapHint] = useState(false);
  const [activeVideo, setActiveVideo] = useState<'A' | 'B'>('A');

  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const readyFiredRef = useRef(false);
  const hasFirstFrameRef = useRef(false);
  const activeVideoRef = useRef<'A' | 'B'>('A');
  const standbyPrimedRef = useRef(false);
  const standbyStartedRef = useRef(false);
  const swappingRef = useRef(false);

  const fireReady = useCallback(() => {
    if (!readyFiredRef.current) {
      readyFiredRef.current = true;
      onReadyRef.current?.();
    }
  }, []);

  // Reset everything when src changes
  useEffect(() => {
    readyFiredRef.current = false;
    hasFirstFrameRef.current = false;
    standbyPrimedRef.current = false;
    standbyStartedRef.current = false;
    swappingRef.current = false;
    activeVideoRef.current = 'A';
    setHasFirstFrame(false);
    setShowTapHint(false);
    setActiveVideo('A');

    const vB = videoBRef.current;
    if (vB) {
      vB.removeAttribute('src');
      vB.load();
    }
  }, [src]);

  useEffect(() => {
    const vA = videoARef.current;
    const vB = videoBRef.current;
    if (!vA || !vB) return;

    // ── First-frame detection (initial load only) ───────────────────────────

    const onCanPlay = () => fireReady();

    const onLoadedMetadata = () => fireReady();

    const onPlaying = () => {
      if (!hasFirstFrameRef.current) {
        hasFirstFrameRef.current = true;
        setHasFirstFrame(true);
        setShowTapHint(false);
      }
      fireReady();
    };

    const onLoadedData = () => {
      if (!hasFirstFrameRef.current && vA.readyState >= 2) {
        hasFirstFrameRef.current = true;
        setHasFirstFrame(true);
        setShowTapHint(false);
      }
    };

    const handleVideoError = (e: Event) => {
      const target = e.target as HTMLVideoElement;
      if (!target.src || target.src === window.location.href) return;
      const err = target.error;
      const code = err?.code ?? 'unknown';
      const msg = err?.message ?? 'no message';
      const srcUrl = target.src;
      const isMov = srcUrl.toLowerCase().includes('.mov');
      const isNotSafari = !/^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (code === 4 && isMov && isNotSafari) {
        console.error(`[SeamlessVideo] .MOV skrá er ekki studd í þessum vafra (aðeins Safari). Vinsamlegast hladdu upp MP4 (H.264) í staðinn. src: ${srcUrl.substring(0, 120)}`);
      } else {
        console.error(`[SeamlessVideo] video load error — code: ${code}, message: ${msg}, src: ${srcUrl.substring(0, 120)}`);
      }
      onErrorRef.current?.();
    };

    // ── Helpers ──────────────────────────────────────────────────────────────

    const getVideos = () => {
      const isA = activeVideoRef.current === 'A';
      return {
        active: isA ? vA : vB,
        standby: isA ? vB : vA,
        isA,
      };
    };

    // How far ahead (seconds) to assign src to standby so it can buffer
    const PRIME_AHEAD = 5;
    // How far ahead (seconds) to start playing standby (hidden behind active)
    const START_AHEAD = 0.18;

    const primeStandby = () => {
      if (standbyPrimedRef.current) return;
      const { standby } = getVideos();
      if (!standby.src || standby.src === window.location.href) {
        standby.src = src;
        standby.load();
      }
      standbyPrimedRef.current = true;
    };

    const startStandby = () => {
      if (standbyStartedRef.current) return;
      standbyStartedRef.current = true;
      const { standby } = getVideos();
      standby.currentTime = 0;
      standby.play().catch(() => {});
    };

    const doSwap = () => {
      if (swappingRef.current) return;
      swappingRef.current = true;

      const { active, isA } = getVideos();
      const next: 'A' | 'B' = isA ? 'B' : 'A';

      // Instant z-index swap — no opacity change, completely invisible
      activeVideoRef.current = next;
      setActiveVideo(next);

      // Pause the old active after a brief moment (it's now hidden behind)
      setTimeout(() => {
        active.pause();
        // Reset flags for the next cycle
        standbyPrimedRef.current = false;
        standbyStartedRef.current = false;
        swappingRef.current = false;
      }, 300);
    };

    // ── timeupdate drives the whole cycle ───────────────────────────────────

    const onTimeUpdate = (e: Event) => {
      const vid = e.target as HTMLVideoElement;
      const { active } = getVideos();

      // Only care about the currently active video's timeupdate
      if (vid !== active) return;
      if (!active.duration || !isFinite(active.duration)) return;

      const timeLeft = active.duration - active.currentTime;

      // Phase 1: assign src to standby so browser starts buffering
      if (timeLeft <= PRIME_AHEAD && !standbyPrimedRef.current) {
        primeStandby();
      }

      // Phase 2: start playing standby (behind active) right before the end
      if (timeLeft <= START_AHEAD && !standbyStartedRef.current) {
        startStandby();
      }
    };

    // When active video actually ends, do the swap
    const onEnded = (e: Event) => {
      const vid = e.target as HTMLVideoElement;
      const { active } = getVideos();
      if (vid !== active) return;

      // Ensure standby is playing
      if (!standbyStartedRef.current) startStandby();
      doSwap();
    };

    // ── Attach events ───────────────────────────────────────────────────────

    vA.addEventListener('canplay', onCanPlay);
    vA.addEventListener('loadedmetadata', onLoadedMetadata);
    vA.addEventListener('playing', onPlaying);
    vA.addEventListener('loadeddata', onLoadedData);
    vA.addEventListener('error', handleVideoError);
    vA.addEventListener('timeupdate', onTimeUpdate);
    vA.addEventListener('ended', onEnded);

    vB.addEventListener('error', handleVideoError);
    vB.addEventListener('timeupdate', onTimeUpdate);
    vB.addEventListener('ended', onEnded);

    // ── Attempt autoplay ───────────────────────────────────────────────────

    const tryPlay = () => vA?.play().catch(() => {});

    tryPlay();
    const r1 = setTimeout(tryPlay, 300);
    const r2 = setTimeout(tryPlay, 1200);

    const fallbackReady = setTimeout(fireReady, 3000);

    const tapHintTimeout = setTimeout(() => {
      if (!hasFirstFrameRef.current) setShowTapHint(true);
    }, 2500);

    const onGesture = () => {
      const { active } = getVideos();
      if (active?.paused) active.play().catch(() => {});
    };
    document.addEventListener('touchstart', onGesture, { once: true, passive: true });
    document.addEventListener('click', onGesture, { once: true });
    document.addEventListener('scroll', onGesture, { once: true, passive: true });

    return () => {
      clearTimeout(r1);
      clearTimeout(r2);
      clearTimeout(fallbackReady);
      clearTimeout(tapHintTimeout);

      vA.removeEventListener('canplay', onCanPlay);
      vA.removeEventListener('loadedmetadata', onLoadedMetadata);
      vA.removeEventListener('playing', onPlaying);
      vA.removeEventListener('loadeddata', onLoadedData);
      vA.removeEventListener('error', handleVideoError);
      vA.removeEventListener('timeupdate', onTimeUpdate);
      vA.removeEventListener('ended', onEnded);
      vB.removeEventListener('error', handleVideoError);
      vB.removeEventListener('timeupdate', onTimeUpdate);
      vB.removeEventListener('ended', onEnded);

      document.removeEventListener('touchstart', onGesture);
      document.removeEventListener('click', onGesture);
      document.removeEventListener('scroll', onGesture);
    };
  }, [src, fireReady]);

  return (
    <div
      className="relative w-full h-full overflow-hidden bg-black cursor-pointer"
      onClick={() => {
        const active = activeVideo === 'A' ? videoARef.current : videoBRef.current;
        if (active?.paused) active.play().catch(() => {});
      }}
    >
      {/* Buffering indicator — visible until first frame */}
      {!hasFirstFrame && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-8 h-px bg-white/20 animate-pulse" />
        </div>
      )}

      {/* "Tap to play" hint if autoplay blocked */}
      {showTapHint && !hasFirstFrame && (
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-16 z-10 pointer-events-none">
          <div className="flex flex-col items-center gap-2 animate-pulse">
            <div className="w-px h-6 bg-white/30" />
            <span className="text-white/40 text-[10px] tracking-[0.3em] uppercase">tap to play</span>
          </div>
        </div>
      )}

      {/* Video A — plays first, always full opacity */}
      <video
        ref={videoARef}
        src={src}
        muted
        playsInline
        preload="auto"
        disablePictureInPicture
        className={`absolute inset-0 w-full h-full object-cover ${className}`}
        style={{
          ...style,
          // Initial fade-in only; after that always 1
          opacity: hasFirstFrame ? 1 : 0,
          transition: hasFirstFrame ? 'none' : `opacity ${fadeDuration}s ease-in-out`,
          zIndex: activeVideo === 'A' ? 2 : 1,
        }}
        onContextMenu={(e) => e.preventDefault()}
      />

      {/* Video B — src assigned lazily, always full opacity, sits behind until swap */}
      <video
        ref={videoBRef}
        muted
        playsInline
        preload="auto"
        disablePictureInPicture
        className={`absolute inset-0 w-full h-full object-cover ${className}`}
        style={{
          ...style,
          opacity: 1,
          zIndex: activeVideo === 'B' ? 2 : 1,
        }}
        onContextMenu={(e) => e.preventDefault()}
      />
    </div>
  );
}